import { ClientTypeE } from './../../models/enums';
import { AccessLevelE, StatusE, UserRoleE } from "../../models/enums";
import { ActionDataT, ProjectT, UserT } from "../../models/models";

type ActionsT = {
    download?: (e: any) => void;
    upload?: (e: any) => void;
    copy?: () => void;
    edit?: () => void;
    delete?: () => void;
    cancel?: () => void;
    reject?: () => void;
    close?: () => void;
    settings?: () => void;
    sendToPost?: () => void;
    sendToBroadcast?: (campaignId: number) => void;
    sendToCustomerApproval?: () => void;
    reBroadcast?: () => void;
};

export const getAvailableActions = (project: ProjectT, user: UserT, actions: ActionsT): ActionDataT[] => {
    return project ? [
        {
            name: 'Download',
            icon: 'download',
            accessFor: [UserRoleE.PostProduction, UserRoleE.Broadcaster, UserRoleE.GlobalAdmin],
            action: actions?.download,
            condition: (
                project.StatusName !== StatusE.Creating &&
                project.StatusName !== StatusE.Opened &&
                project.StatusName !== StatusE.Editing &&
                project.StatusName !== StatusE.UploadingFiles
            ),
        },
        {
            name: 'Send to Post Production',
            icon: 'sendPostProd',
            accessFor: [UserRoleE.AdvertisingAgency],
            action: actions?.sendToPost,
            condition: project.StatusName === StatusE.Creating,
        },
        // {
        //     name: 'Send to Broadcasters',
        //     icon: 'sendBroadcast',
        //     accessFor: [UserRoleE.AdvertisingAgency],
        //     action: actions?.sendToBroadcast,
        //     condition: project.StatusName === StatusE.WaitingForApprovalOfAdvertisingAgency,
        // },
        {
            name: 'Send to customer approval',
            icon: 'send',
            accessFor: [UserRoleE.AdvertisingAgency],
            action: actions?.sendToCustomerApproval,
            condition: project.StatusName === StatusE.WaitingForApprovalOfAdvertisingAgency,
        },
        {
            name: 'Upload',
            icon: 'upload',
            accessFor: [UserRoleE.PostProduction],
            condition: user.accessLevel !== AccessLevelE.GlobalAdmin && (
                project.StatusName === StatusE.Creating ||
                project.StatusName === StatusE.Opened ||
                project.StatusName === StatusE.Editing ||
                project.StatusName === StatusE.UploadingFiles
            ),
            action: actions?.upload,
        },
        {
            name: 'Clone',
            icon: 'copy',
            accessFor: [UserRoleE.AdvertisingAgency],
            condition: (
                (user.accessLevel !== AccessLevelE.GlobalAdmin) && (
                    project.StatusName !== StatusE.Creating &&
                    project.StatusName !== StatusE.Opened &&
                    project.StatusName !== StatusE.Editing &&
                    project.StatusName !== StatusE.UploadingFiles
                )),
            action: actions?.copy,
        },
        {
            // Entry (b): re-broadcast / reuse this past delivery (opens the re-delivery builder). Same
            // eligibility gate as the server: a non-guest advertising agency, on a delivery that was
            // already downloaded (by a/all broadcasters) or closed. A delivery only "sent and pending
            // download" (ApprovedAndSentToBroadcaster) is intentionally NOT reusable. Full eligibility
            // (30-day age etc.) is enforced server-side on lookup.
            name: 'Reuse this delivery',
            icon: 'reload',
            accessFor: [UserRoleE.AdvertisingAgency],
            condition: (
                (project.StatusName === StatusE.DownloadedByBroadcaster ||
                    project.StatusName === StatusE.DownloadedByAllBroadcasters ||
                    project.StatusName === StatusE.Closed)
                && user.accessLevel !== AccessLevelE.Guest
                && user.accessLevel !== AccessLevelE.GlobalAdmin
            ),
            action: actions?.reBroadcast,
        },
        {
            name: 'Edit delivery details',
            icon: 'edit',
            accessFor: [UserRoleE.AdvertisingAgency, UserRoleE.GlobalAdmin, UserRoleE.GlobalAdminAndAdminContentManager, UserRoleE.Broadcaster, UserRoleE.MediaCompany],
            condition: user.role === UserRoleE.AdvertisingAgency ? (
                project.StatusName !== StatusE.UploadingFiles
                && project.StatusName !== StatusE.Deleted
                && project.StatusName !== StatusE.Cancelled
                && project.StatusName !== StatusE.Closed
                && project.StatusName !== StatusE.Editing
            ) : user.role === UserRoleE.Broadcaster
                ? project.StatusName === StatusE.InnerCampaign
                : user.role === UserRoleE.MediaCompany
                    // A media-company user may open the delivery editor (to reassign the campaign
                    // association) for their own deliveries; guests are filtered out by accessLevel
                    // wherever these actions render. Exclude only metadata/inner pseudo-deliveries.
                    ? (user.accessLevel !== AccessLevelE.Guest
                        && project.StatusName !== StatusE.Metadata
                        && project.StatusName !== StatusE.InnerCampaign)
                    : project.StatusName !== StatusE.Metadata,
            action: actions?.edit,
        },
        {
            name: 'Delete',
            icon: 'bin',
            accessFor: [UserRoleE.AdvertisingAgency],
            condition: (
                project.StatusName === StatusE.Cancelled
                && user.accessLevel !== AccessLevelE.GlobalAdmin
                && user.role !== UserRoleE.GlobalAdminAndAdminContentManager
            ),
            action: actions?.delete,
        },
        {
            name: 'Cancel this delivery',
            icon: 'cancel',
            accessFor: [UserRoleE.AdvertisingAgency, UserRoleE.DeliveryService],
            condition: (
                project.StatusName === StatusE.Creating ||
                project.StatusName === StatusE.Opened ||
                project.StatusName === StatusE.Editing ||
                project.StatusName === StatusE.UploadingFiles ||
                project.StatusName === StatusE.WaitingForApprovalOfPostProduction ||
                project.StatusName === StatusE.WaitingForApprovalOfAdvertisingAgency
            ) && (user.accessLevel !== AccessLevelE.GlobalAdmin && user.role !== UserRoleE.GlobalAdminAndAdminContentManager)
                && (
                    user.clientType === ClientTypeE['Advertising Agency']
                    || user.accessLevel === AccessLevelE.DeliveryServicePrivate
                    // && +user.id === +project.AdvertisingAgencyId
                ),
            action: actions?.cancel,
        },
        {
            name: 'Reject this delivery',
            icon: 'close',
            accessFor: [UserRoleE.AdvertisingAgency],
            condition: (
                project.StatusName === StatusE.ApprovedAndSentToBroadcaster ||
                project.StatusName === StatusE.DownloadedByAllBroadcasters ||
                project.StatusName === StatusE.DownloadedByBroadcaster
            ) && (user.accessLevel !== AccessLevelE.GlobalAdmin && user.role !== UserRoleE.GlobalAdminAndAdminContentManager),
            action: actions?.reject,
        },
        {
            name: 'Close',
            icon: 'close',
            accessFor: [UserRoleE.Broadcaster],
            condition: project.StatusName === StatusE.ApprovedAndSentToBroadcaster,
            action: actions?.close,
        },
        {
            name: 'Settings',
            icon: 'settings',
            accessFor: [UserRoleE.AdvertisingAgency],
            condition: user.accessLevel !== AccessLevelE.GlobalAdmin,
            action: actions?.settings,
        },
    ] : [];
};